/**
 * PostHog Analytics Configuration
 * 
 * SECURITY: This file configures PostHog with privacy-first settings.
 * - All inputs are masked by default
 * - Network request bodies/headers are NOT recorded (prevents token capture)
 * - Requires explicit user consent (GDPR opt-in mode)
 * - Respects Do Not Track browser setting
 * 
 * @see https://posthog.com/docs/libraries/react
 * @see https://posthog.com/docs/session-replay/privacy
 */

import posthog from 'posthog-js';

// PostHog configuration
const POSTHOG_KEY = import.meta.env.VITE_POSTHOG_KEY;
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

// Storage keys for consent management
export const CONSENT_STORAGE_KEY = 'ulearning_analytics_consent';
export const CONSENT_TIMESTAMP_KEY = 'ulearning_analytics_consent_timestamp';

/**
 * Check if user has given analytics consent
 */
export function hasAnalyticsConsent(): boolean {
  try {
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    return consent === 'granted';
  } catch {
    return false;
  }
}

/**
 * Check if user has explicitly declined consent
 */
export function hasDeclinedConsent(): boolean {
  try {
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    return consent === 'denied';
  } catch {
    return false;
  }
}

/**
 * Check if consent decision has been made (either way)
 */
export function hasConsentDecision(): boolean {
  try {
    const consent = localStorage.getItem(CONSENT_STORAGE_KEY);
    return consent === 'granted' || consent === 'denied';
  } catch {
    return false;
  }
}

/**
 * Grant analytics consent and enable tracking
 */
export function grantConsent(): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'granted');
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    
    // Enable PostHog tracking
    if (typeof posthog !== 'undefined' && posthog.opt_in_capturing) {
      posthog.opt_in_capturing();
    }
  } catch (error) {
    console.error('Failed to save consent:', error);
  }
}

/**
 * Deny analytics consent and disable tracking
 */
export function denyConsent(): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'denied');
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    
    // Disable PostHog tracking
    if (typeof posthog !== 'undefined' && posthog.opt_out_capturing) {
      posthog.opt_out_capturing();
    }
  } catch (error) {
    console.error('Failed to save consent denial:', error);
  }
}

/**
 * Revoke consent and clear analytics data
 */
export function revokeConsent(): void {
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, 'denied');
    localStorage.setItem(CONSENT_TIMESTAMP_KEY, new Date().toISOString());
    
    // Disable PostHog and reset identity
    if (typeof posthog !== 'undefined') {
      posthog.opt_out_capturing();
      posthog.reset();
    }
  } catch (error) {
    console.error('Failed to revoke consent:', error);
  }
}

/**
 * Initialize PostHog with privacy-first configuration
 * 
 * SECURITY NOTES:
 * - opt_out_capturing_by_default: true - No tracking until consent
 * - maskAllInputs: true - All form inputs are masked
 * - recordHeaders/recordBody: false - Prevents auth token capture
 * - respect_dnt: true - Honors browser Do Not Track
 * - secure_cookie: true - Cookies only over HTTPS
 */
export function initializePostHog(): void {
  // Don't initialize if no API key (development without key)
  if (!POSTHOG_KEY) {
    console.warn('PostHog: API key not configured. Analytics disabled.');
    return;
  }

  // Don't initialize on server-side
  if (typeof window === 'undefined') {
    return;
  }

  // Check if already initialized
  if (posthog.__loaded) {
    return;
  }

  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      
      // ========================================
      // GDPR COMPLIANCE - Opt-In Mode
      // ========================================
      // No tracking until user explicitly consents
      opt_out_capturing_by_default: true,
      
      // ========================================
      // SESSION RECORDING PRIVACY
      // ========================================
      session_recording: {
        // Mask ALL input fields by default (passwords, emails, etc.)
        maskAllInputs: true,
        
        // Mask specific input types explicitly
        maskInputOptions: {
          password: true,
          email: true,
          tel: true,
          number: true,
          search: true,
        },
        
        // Mask text content with this CSS class
        maskTextSelector: '.ph-no-capture, [data-ph-no-capture]',
        
        // Block entire elements from being recorded
        blockSelector: '.ph-block, [data-ph-block]',
        
        // CRITICAL: Network capture is disabled by default in PostHog
        // If enabled in PostHog project settings, configure:
        // - Project Settings > Session Recording > Network capture OFF
        // This ensures Authorization headers and JWT tokens are never captured
      },
      
      // ========================================
      // PRIVACY SETTINGS
      // ========================================
      // Respect browser's Do Not Track setting
      respect_dnt: true,
      
      // Don't track across subdomains
      cross_subdomain_cookie: false,
      
      // Use secure cookies (HTTPS only in production)
      secure_cookie: import.meta.env.PROD,
      
      // Cookie settings
      cookie_expiration: 365, // 1 year
      
      // ========================================
      // AUTOCAPTURE SETTINGS
      // ========================================
      // Enable autocapture but with privacy in mind
      autocapture: {
        // Capture clicks, but respect ph-no-capture
        dom_event_allowlist: ['click', 'submit'],
        
        // Don't capture input change events (could leak data)
        capture_copied_text: false,
      },
      
      // ========================================
      // PERFORMANCE & BEHAVIOR
      // ========================================
      // Enable session recording only if consent given
      disable_session_recording: !hasAnalyticsConsent(),
      
      // Capture pageviews automatically
      capture_pageview: true,
      
      // Capture page leaves
      capture_pageleave: true,
      
      // Session timeout (30 minutes of inactivity)
      session_idle_timeout_seconds: 1800,
      
      // Don't persist super properties (extra safety)
      persistence: 'localStorage+cookie',
      
      // Bootstrap to prevent flicker
      bootstrap: {
        distinctID: undefined, // Will be set on user identification
      },
      
      // Loaded callback
      loaded: (posthogInstance) => {
        // If user has already consented, opt them in
        if (hasAnalyticsConsent()) {
          posthogInstance.opt_in_capturing();
        }
        
        // Debug mode in development only
        if (import.meta.env.DEV) {
          console.log('PostHog loaded:', posthogInstance.get_distinct_id());
        }
      },
    });
  } catch (error) {
    console.error('PostHog initialization failed:', error);
  }
}

/**
 * Identify user in PostHog (privacy-safe)
 * 
 * SECURITY: Only sends anonymous identifiers, NO PII
 * - Uses Firebase UID as identifier
 * - Only includes role for segmentation
 * - NO email, name, or other personal data
 */
export function identifyUser(userId: string, traits?: {
  role?: 'STUDENT' | 'PROFESSOR' | 'ADMIN';
  createdAt?: string;
}): void {
  if (!hasAnalyticsConsent()) {
    return;
  }

  try {
    posthog.identify(userId, {
      role: traits?.role,
      $set_once: {
        first_seen_at: traits?.createdAt || new Date().toISOString(),
      },
    });
  } catch (error) {
    console.error('PostHog identify failed:', error);
  }
}

/**
 * Reset user identity (call on logout)
 */
export function resetIdentity(): void {
  try {
    posthog.reset();
  } catch (error) {
    console.error('PostHog reset failed:', error);
  }
}

/**
 * Track custom event (privacy-safe)
 * 
 * SECURITY: Sanitizes event properties to prevent accidental PII leakage
 */
export function trackEvent(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!hasAnalyticsConsent()) {
    return;
  }

  try {
    // Sanitize properties to remove any potential PII
    const sanitizedProps = properties ? sanitizeEventProperties(properties) : undefined;
    posthog.capture(eventName, sanitizedProps);
  } catch (error) {
    console.error('PostHog event capture failed:', error);
  }
}

/**
 * Sanitize event properties to prevent accidental PII capture
 */
function sanitizeEventProperties(
  properties: Record<string, unknown>
): Record<string, unknown> {
  const sensitiveKeys = [
    'email', 'password', 'token', 'authorization', 'auth',
    'credit_card', 'card_number', 'cvv', 'cvc', 'ssn',
    'phone', 'address', 'name', 'firstName', 'lastName',
    'first_name', 'last_name', 'dob', 'date_of_birth',
    'secret', 'key', 'apiKey', 'api_key', 'accessToken',
    'access_token', 'refreshToken', 'refresh_token', 'idToken',
    'id_token', 'privateKey', 'private_key',
  ];

  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(properties)) {
    const lowerKey = key.toLowerCase();
    
    // Skip sensitive keys
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    // Recursively sanitize nested objects
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeEventProperties(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Track page view manually (if needed)
 */
export function trackPageView(pageName?: string): void {
  if (!hasAnalyticsConsent()) {
    return;
  }

  try {
    posthog.capture('$pageview', pageName ? { page: pageName } : undefined);
  } catch (error) {
    console.error('PostHog pageview capture failed:', error);
  }
}

/**
 * Set user properties (privacy-safe)
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (!hasAnalyticsConsent()) {
    return;
  }

  try {
    const sanitized = sanitizeEventProperties(properties);
    posthog.people.set(sanitized);
  } catch (error) {
    console.error('PostHog set properties failed:', error);
  }
}

// Export PostHog instance for advanced usage
export { posthog };
